import {
  createBrowserLocalDomainColorPreferenceProvider,
} from "./domain-color-preference-local-provider.js";

export const DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION = 1;
export const DOMAIN_COLOR_BOOT_CACHE_VERSION = 1;
export const DOMAIN_COLOR_MODES = Object.freeze([
  "game",
  "theme-with-game-grades",
  "theme",
]);
export const DEFAULT_DOMAIN_COLOR_MODE = "theme";

const MAX_DOCUMENT_CHARS = 2_048;
const DOCUMENT_FIELDS = Object.freeze(["mode", "revision", "schemaVersion"]);
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.domain-color-preference-repository.v1",
);
const BROWSER_LOCAL_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-local-domain-color-preference-repository.v1",
);
const BROWSER_LOCAL_REPOSITORY_PROPERTY =
  "__fishystuffLocalDomainColorPreferenceRepositoryV1";

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function exactFields(value, expected) {
  const fields = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  return fields.length === sortedExpected.length
    && fields.every((field, index) => field === sortedExpected[index]);
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritative) next.authoritative = freezeState(next.authoritative);
  if (next.legacyCleanup) {
    next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
  }
  return Object.freeze(next);
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function unavailable(operation, reason, authoritative = null) {
  return freezeState({
    kind: "unavailable",
    operation,
    reason,
    ...(authoritative ? { authoritative } : {}),
  });
}

function requireMode(value) {
  if (!DOMAIN_COLOR_MODES.includes(value)) {
    throw new TypeError(
      "Domain color mode must be game, theme-with-game-grades, or theme.",
    );
  }
  return value;
}

function expectedRevisionFrom(options) {
  if (options === undefined) return undefined;
  if (
    !isRecord(options)
    || Object.keys(options).some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError(
      "Domain color command options contain unsupported fields.",
    );
  }
  const revision = options.expectedRevision;
  if (revision === undefined || revision === null) return revision;
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError(
      "Expected domain color revision must be null or a positive safe integer.",
    );
  }
  return revision;
}

export function decodeDomainColorPreferenceDocument(raw) {
  if (typeof raw !== "string") {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-must-be-text",
      raw,
    });
  }
  if (raw.length === 0 || raw.length > MAX_DOCUMENT_CHARS) {
    return freezeState({
      kind: "invalid",
      reason: raw.length === 0
        ? "stored-value-must-not-be-empty"
        : "stored-value-exceeds-v1-limit",
      raw,
    });
  }
  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return freezeState({ kind: "invalid", reason: "malformed-json", raw });
  }
  if (!isRecord(document)) {
    return freezeState({
      kind: "invalid",
      reason: "document-must-be-an-object",
      raw,
    });
  }
  if (
    Number.isSafeInteger(document.schemaVersion)
    && document.schemaVersion > DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION
  ) {
    return freezeState({
      kind: "unsupportedVersion",
      foundVersion: document.schemaVersion,
      raw,
    });
  }
  if (!exactFields(document, DOCUMENT_FIELDS)) {
    return freezeState({
      kind: "invalid",
      reason: "document-fields-must-match-v1",
      raw,
    });
  }
  if (document.schemaVersion !== DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION) {
    return freezeState({
      kind: "invalid",
      reason: "schema-version-must-be-1",
      raw,
    });
  }
  if (!Number.isSafeInteger(document.revision) || document.revision <= 0) {
    return freezeState({
      kind: "invalid",
      reason: "revision-must-be-positive",
      raw,
    });
  }
  if (!DOMAIN_COLOR_MODES.includes(document.mode)) {
    return freezeState({
      kind: "invalid",
      reason: "mode-is-not-supported",
      raw,
    });
  }
  return freezeState({
    kind: "ready",
    revision: document.revision,
    mode: document.mode,
    raw,
  });
}

export function encodeDomainColorPreferenceDocument({ revision, mode }) {
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError("Domain color revision must be a positive safe integer.");
  }
  return JSON.stringify({
    schemaVersion: DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION,
    revision,
    mode: requireMode(mode),
  });
}

export function encodeDomainColorBootCache({ revision, mode }) {
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError(
      "Domain color cache source revision must be a positive safe integer.",
    );
  }
  return JSON.stringify({
    cacheVersion: DOMAIN_COLOR_BOOT_CACHE_VERSION,
    sourceRevision: revision,
    mode: requireMode(mode),
  });
}

function authoritativeState(state) {
  return state?.authoritative ?? state;
}

export function domainColorPreferenceDecision(state) {
  const source = isRecord(state) ? state : { kind: "uninitialized" };
  const authoritative = authoritativeState(source);
  const hasReadyAuthority = authoritative?.kind === "ready";
  return Object.freeze({
    repositoryKind: typeof source.kind === "string"
      ? source.kind
      : "uninitialized",
    revision: hasReadyAuthority ? authoritative.revision : null,
    mode: hasReadyAuthority ? authoritative.mode : DEFAULT_DOMAIN_COLOR_MODE,
    canChange: source.kind === "ready" || source.kind === "missing",
  });
}

export function createDomainColorPreferenceRepository({ provider = null } = {}) {
  if (
    !provider
    || typeof provider.dispose !== "function"
    || typeof provider.readDocument !== "function"
    || typeof provider.readRetiredMode !== "function"
    || typeof provider.removeDocument !== "function"
    || typeof provider.removeRetiredMode !== "function"
    || typeof provider.subscribeInvalidation !== "function"
    || typeof provider.writeDocument !== "function"
  ) {
    throw new TypeError(
      "Domain color repository requires its exact local provider.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return freezeState(currentState);
  }

  function decision() {
    return domainColorPreferenceDecision(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return state();
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) deliver(listener);
    }
    return state();
  }

  function readAuthoritativeState() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable(stored.operation, stored.reason);
    }
    return stored.raw === null
      ? freezeState({ kind: "missing", raw: null })
      : decodeDomainColorPreferenceDocument(stored.raw);
  }

  function withLegacyCleanupState(nextState) {
    const cleanup = provider.removeRetiredMode();
    if (cleanup.kind !== "unavailable") return nextState;
    return freezeState({
      ...nextState,
      legacyCleanup: {
        kind: "unavailable",
        operation: "remove",
        reason: cleanup.reason,
      },
    });
  }

  function publishAuthoritative(authoritative) {
    return publish(withLegacyCleanupState(authoritative));
  }

  function writeReady(revision, mode, previousRaw) {
    const beforeWrite = provider.readDocument();
    if (beforeWrite.kind === "unavailable") {
      return publish(unavailable(
        beforeWrite.operation,
        beforeWrite.reason,
      ));
    }
    if (beforeWrite.raw !== previousRaw) {
      return conflict(revision === 1 ? null : revision - 1, readAuthoritativeState());
    }

    const raw = encodeDomainColorPreferenceDocument({ revision, mode });
    const written = provider.writeDocument(raw);
    if (written.kind === "unavailable") {
      const authoritative = previousRaw === null
        ? freezeState({ kind: "missing", raw: null })
        : decodeDomainColorPreferenceDocument(previousRaw);
      return publish(unavailable(
        written.operation,
        written.reason,
        authoritative,
      ));
    }
    return publishAuthoritative(decodeDomainColorPreferenceDocument(raw));
  }

  function load() {
    if (disposed) return state();
    publish({ kind: "loading" });
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "ready") {
      return publishAuthoritative(authoritative);
    }
    if (authoritative.kind !== "missing") {
      return publish(authoritative);
    }

    const retired = provider.readRetiredMode();
    if (retired.kind === "unavailable") {
      return publish(unavailable(
        retired.operation,
        retired.reason,
      ));
    }
    if (retired.kind === "missing") {
      return publish(authoritative);
    }
    if (retired.kind === "invalid") {
      return publish({
        kind: "invalid",
        reason: retired.reason,
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    if (!DOMAIN_COLOR_MODES.includes(retired.value)) {
      return publish({
        kind: "invalid",
        reason: "retired-domain-color-mode-is-not-supported",
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    return writeReady(1, retired.value, null);
  }

  function retiredMutationBlock() {
    const retired = provider.readRetiredMode();
    if (retired.kind === "unavailable") {
      return unavailable(retired.operation, retired.reason);
    }
    if (retired.kind === "invalid") {
      return freezeState({
        kind: "invalid",
        reason: retired.reason,
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    if (
      retired.kind === "candidate"
      && !DOMAIN_COLOR_MODES.includes(retired.value)
    ) {
      return freezeState({
        kind: "invalid",
        reason: "retired-domain-color-mode-is-not-supported",
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    return null;
  }

  function conflict(expectedRevision, authoritative) {
    const exactAuthoritative = authoritativeState(authoritative);
    return publish({
      kind: "conflict",
      expectedRevision,
      actualRevision: exactAuthoritative?.kind === "ready"
        ? exactAuthoritative.revision
        : null,
      authoritative: exactAuthoritative,
    });
  }

  async function setMode(mode, options = {}) {
    const exactMode = requireMode(mode);
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (!["missing", "ready"].includes(authoritative.kind)) {
      return publish(authoritative);
    }
    if (authoritative.kind === "missing") {
      const blocked = retiredMutationBlock();
      if (blocked) return publish(blocked);
    }
    const actualRevision = authoritative.kind === "ready"
      ? authoritative.revision
      : null;
    if (
      expectedRevision !== undefined
      && expectedRevision !== actualRevision
    ) {
      if (authoritative.kind === "ready" && authoritative.mode === exactMode) {
        return publishAuthoritative(authoritative);
      }
      return conflict(expectedRevision, authoritative);
    }
    if (authoritative.kind === "ready" && authoritative.mode === exactMode) {
      return publishAuthoritative(authoritative);
    }
    if (
      authoritative.kind === "ready"
      && authoritative.revision >= Number.MAX_SAFE_INTEGER
    ) {
      return publish(unavailable(
        "write",
        "revision-exhausted",
        authoritative,
      ));
    }

    const nextRevision = actualRevision === null ? 1 : actualRevision + 1;
    publish({
      kind: "pending",
      requestedMode: exactMode,
      baseRevision: actualRevision,
      authoritative,
    });
    return writeReady(nextRevision, exactMode, authoritative.raw);
  }

  async function exportRecord() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable("export", stored.reason);
    }
    const records = [];
    if (stored.raw !== null) {
      const decoded = decodeDomainColorPreferenceDocument(stored.raw);
      records.push(Object.freeze({
        source: "canonical",
        recordKind: decoded.kind,
        raw: stored.raw,
      }));
    }
    const retired = provider.readRetiredMode();
    if (retired.kind === "unavailable") {
      return unavailable("export", retired.reason);
    }
    if (retired.kind !== "missing") {
      records.push(Object.freeze({
        source: "retired-ui-settings",
        recordKind: retired.kind === "candidate"
          && DOMAIN_COLOR_MODES.includes(retired.value)
          ? "retired"
          : "retiredInvalid",
        raw: retired.raw,
      }));
    }
    return records.length === 0
      ? freezeState({ kind: "missing" })
      : Object.freeze({ kind: "exported", records: Object.freeze(records) });
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "unavailable") return publish(authoritative);
    if (
      expectedRevision !== undefined
      && !["missing", "ready"].includes(authoritative.kind)
    ) {
      return publish(authoritative);
    }
    const actualRevision = authoritative.kind === "ready"
      ? authoritative.revision
      : null;
    if (
      expectedRevision !== undefined
      && expectedRevision !== actualRevision
    ) {
      return conflict(expectedRevision, authoritative);
    }

    const retired = provider.removeRetiredMode();
    if (retired.kind === "unavailable") {
      return publish(unavailable(
        retired.operation,
        retired.reason,
        authoritative,
      ));
    }
    const removed = provider.removeDocument();
    if (removed.kind === "unavailable") {
      return publish(unavailable(
        removed.operation,
        removed.reason,
        authoritative,
      ));
    }
    return publish({ kind: "missing", raw: null });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Domain color repository listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeInvalidation = provider.subscribeInvalidation(() => load());

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeInvalidation();
    listeners.clear();
    provider.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION,
    decision,
    dispose,
    exportRecord,
    load,
    reset,
    setMode,
    state,
    subscribe,
  });
}

function compatibleBrowserRepository(value) {
  return Boolean(
    value
    && value[BROWSER_LOCAL_REPOSITORY_BRAND]
      === DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION
    && typeof value.decision === "function"
    && typeof value.exportRecord === "function"
    && typeof value.load === "function"
    && typeof value.reset === "function"
    && typeof value.setMode === "function"
    && typeof value.state === "function"
    && typeof value.subscribe === "function",
  );
}

function browserRepositoryView(repository) {
  return Object.freeze({
    [BROWSER_LOCAL_REPOSITORY_BRAND]: DOMAIN_COLOR_PREFERENCE_SCHEMA_VERSION,
    decision: repository.decision,
    exportRecord: repository.exportRecord,
    load: repository.load,
    reset: repository.reset,
    setMode: repository.setMode,
    state: repository.state,
    subscribe: repository.subscribe,
  });
}

let browserLocalRepository = null;

export function browserLocalDomainColorPreferenceRepository() {
  const browser = globalThis.window ?? globalThis;
  const shared = browser?.[BROWSER_LOCAL_REPOSITORY_PROPERTY];
  if (shared !== undefined) {
    if (!compatibleBrowserRepository(shared)) {
      throw new TypeError(
        "The shared domain color repository has an incompatible contract.",
      );
    }
    browserLocalRepository = shared;
  }
  if (!browserLocalRepository) {
    const repository = createDomainColorPreferenceRepository({
      provider: createBrowserLocalDomainColorPreferenceProvider(),
    });
    repository.load();
    browserLocalRepository = browserRepositoryView(repository);
  }
  if (browser && (typeof browser === "object" || typeof browser === "function")) {
    Object.defineProperty(browser, BROWSER_LOCAL_REPOSITORY_PROPERTY, {
      configurable: true,
      value: browserLocalRepository,
      writable: false,
    });
  }
  return browserLocalRepository;
}
